import { Component, AfterViewInit, Input, ChangeDetectorRef, SimpleChanges } from '@angular/core';
import { ChartData, ChartOptions, ChartConfiguration } from 'chart.js';
import { environment } from '@portals/environments';
import { BaseComponent } from '@portals/core';
import { format } from 'date-fns';
import { finalize, tap, catchError, of } from 'rxjs';

import { CollectiblesState, AuthState, ChannelsState } from '@portals/store';

interface DistributionData {
	channel_name: string;
	total_count: number;
	percentage: number;
}

interface AverageData {
	average_daily_follows: number;
}

interface GrowthData {
	current_total: number;
	previous_total: number;
	growth_rate: number;
	previous_start_date: string;
	previous_end_date: string;
	current_start_date: string;
	current_end_date: string;
}
interface StreakData {
	streak_length: string;
	streak_start: string;
	streak_end: string;
}
@Component({
	selector: 'portals-follow-chart',
	templateUrl: './follow-chart.component.html',
	styleUrls: ['./follow-chart.component.scss'],
})
export class FollowChartComponent extends BaseComponent implements AfterViewInit {
	@Input() projectID!: number;
	@Input() timeframe = 'yearly';
	indexMap: any = {};
	mapEntires: any[] = [];
	totalIdx = 0;
	tableData: any = [];
	averageData: AverageData = {
		average_daily_follows: 0,
	};
	growthData: GrowthData = {
		current_total: 0,
		previous_total: 0,
		growth_rate: 0,
		previous_start_date: '',
		previous_end_date: '',
		current_start_date: '',
		current_end_date: '',
	};
	streakData: StreakData = {
		streak_end: '0',
		streak_length: '',
		streak_start: '',
	};
	@Input() currentFilters!: any;

	ngOnChanges(changes: SimpleChanges) {
		if (changes['currentFilters']) {
			const previousValue = changes['currentFilters'].previousValue;
			const currentValue = changes['currentFilters'].currentValue;

			if (!this.isEqual(previousValue, currentValue)) {
				this.getChart(this.timeframe, currentValue);
				this.cdr.detectChanges();
			}
		}
	}
	public lineChartData: ChartData<'line'> = {
		datasets: [],
		labels: [],
	};

	constructor(private authState: AuthState, private channel: ChannelsState, private cdr: ChangeDetectorRef) {
		super();
	}
	ngAfterViewInit() {
		this.getChart(this.timeframe, this.currentFilters);
	}
	public lineChartOptions: ChartOptions<'line'> = {
		responsive: true,
		maintainAspectRatio: false,
		elements: {
			point: {
				radius: 0,
				hitRadius: 10,
			},
		},
		plugins: {
			legend: {
				display: false, // Ensure legend is hidden
				fullSize: false,
				labels: {
					boxHeight: 10,
					boxWidth: 10,
					padding: 10,
					pointStyle: 'circle',
					usePointStyle: true,
				},
			},
		},
		scales: {
			x: {
				grid: {
					display: false,
				},
				ticks: {
					maxRotation: 0,
					minRotation: 0,
					autoSkip: true,
					maxTicksLimit: 10,
				},
			},
			y: {
				grid: {
					display: true,
				},
			},
		},
	};
	public hiddenMap: any = {};
	public lineChartLegend = true;
	public lineChartPlugins = [];
	timeframes = ['daily', 'weekly', 'monthly', 'yearly'];
	formLoading = false;
	app = environment.app;
	admin = false;
	dateInfo = new Date();
	totalCount = 0;
	showOnlyTotal = false;
	async getChart(timeframe: string, filters: any) {
		this.formLoading = true;
		this.hiddenMap = {};
		this.mapEntires = [];
		this.tableData = [];
		this.subscribe(
			this.channel.getFollowData(timeframe, this.projectID, filters).pipe(
				tap((data: any) => {
					try {
						if (!data || typeof data !== 'object') {
							throw new Error('Invalid data received from server');
						}

						if (!data.main_data || !Array.isArray(data.main_data.data) || !Array.isArray(data.main_data.labels)) {
							throw new Error('Invalid main_data structure');
						}

						this.series = data.main_data.data;
						this.date = data.main_data.labels;

						let intTotalCount = 0;
						this.totalCount = intTotalCount;

						if (data.average_data && !Array.isArray(data.average_data) && data.average_data.length > 0) {
							this.averageData = data.average_data[0];
						}

						if (data.growth && Array.isArray(data.growth) && data.growth.length > 0) {
							this.growthData = data.growth[0];
						}
						if (data.streak_data && Array.isArray(data.streak_data) && data.streak_data.length > 0) {
							this.streakData = data.streak_data[0];
						}
					} catch (error) {
						console.error('Error processing data:', error);
						this.errorMessage = 'An error occurred while processing the data. Please try again later.';
						throw error; // Re-throw to trigger the catchError
					}
				}),
				catchError((error) => {
					console.error('Error in getFollowData:', error);
					this.errorMessage = 'An error occurred while fetching the data. Please try again later.';
					return of(null); // Return an observable with a null value to continue the stream
				}),
				finalize(() => {
					this.labels = this.date;
					const dataset = this.series.map((i: any) => {
						if (i.label !== 'total') {
							return { label: i.label, data: i.data, tension: 0.4, borderColor: this.getRandomColor() };
						} else {
							return { label: i.label, data: i.data, tension: 0.4, borderColor: '#491176' };
						}
					});
					const dates = this.labels.map((i: any) => format(new Date(i), 'MMM dd'));
					this.lineChartData = {
						datasets: dataset,
						labels: this.date,
					};
					let totalCountI = 0;
					dataset.forEach((i: any, j: any) => {
						if (i.label !== 'total') {
							const count = i.data.reduce((a: any, b: any) => a + b);
							this.indexMap[j] = { label: i.label, color: i.borderColor, total: count };
							totalCountI += count;
							this.tableData.push([i.label, count]);
						} else {
							this.totalIdx = j;
						}
					});
					this.totalCount = totalCountI;
					this.mapEntires = Object.entries(this.indexMap);
					this.formLoading = false;
				})
			)
		);
	}
	private isEqual(obj1: any, obj2: any): boolean {
		return JSON.stringify(obj1) === JSON.stringify(obj2);
	}
	ngOnInit(): void {}
	changeTimeframe(event: any) {
		this.timeframe = event.target.value;
		this.getChart(this.timeframe, this.currentFilters);
	}
	toggleTotalOnly(): void {
		let lineChartDataInt = this.lineChartData;
		for (let i = 0; i < lineChartDataInt.datasets.length; i++) {
			const m = lineChartDataInt.datasets[i];
			if (m.label !== 'total') {
				lineChartDataInt.datasets[i].hidden = this.showOnlyTotal ? true : false;
			}
		}
		this.lineChartData = lineChartDataInt;
	}
	checkIfHidden(idx: number) {
		if (this.hiddenMap[idx]) {
			return true;
		} else {
			return false;
		}
	}
	toggleLine(idx: number): void {
		const newData = this.lineChartData;
		if (this.hiddenMap[idx]) {
			newData.datasets[idx].hidden = false;
			delete this.hiddenMap[idx];
		} else {
			newData.datasets[idx].hidden = true;
			this.hiddenMap[idx] = 1;
		}
		console.log(newData.datasets);
		console.log(this.hiddenMap);
		this.lineChartData = { ...newData };
	}
	onBadgeClick(idx: number): void {
		this.toggleLine(idx);
	}
	private getRandomColor(): string {
		const letters = '0123456789ABCDEF';
		let color = '#';
		for (let i = 0; i < 6; i++) {
			color += letters[Math.floor(Math.random() * 16)];
		}
		return color;
	}
}
